import { AnimatePresence, animate, motion, type PanInfo, useMotionValue } from "framer-motion";
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export type SnapZone = "top-left" | "top-right" | "center" | "bottom-left" | "bottom-right";

const ZONES: SnapZone[] = ["top-left", "top-right", "center", "bottom-left", "bottom-right"];

/** Gap between a snapped card and the viewport edge. */
const MARGIN = 16;

/** Snappy spring with a little settle — feels physical without being bouncy. */
const SNAP_SPRING = { type: "spring", stiffness: 420, damping: 34 } as const;

/** Release speed (px/s) above which a gesture reads as a deliberate flick. */
const FLICK_SPEED = 200;

/**
 * How far ahead of the pointer we look, in seconds, when projecting momentum
 * (roughly iOS's "normal" scroll deceleration). A flick at 1500px/s looks
 * ~525px ahead, enough to carry the card across the screen.
 */
const VELOCITY_PROJECTION = 0.35;

/**
 * Hysteresis for the landing preview: the highlighted zone only changes once
 * a new zone is clearly closer (its distance under 70% of the current
 * zone's). Without this, hovering near the midline between two zones makes
 * the preview flicker back and forth with every pixel of hand jitter.
 */
const ZONE_STICKINESS = 0.7;

interface Size {
  width: number;
  height: number;
}

interface Velocity {
  x: number;
  y: number;
}

function zonePosition(zone: SnapZone, card: Size): { x: number; y: number } {
  const maxX = window.innerWidth - card.width - MARGIN;
  const maxY = window.innerHeight - card.height - MARGIN;
  switch (zone) {
    case "top-left":
      return { x: MARGIN, y: MARGIN };
    case "top-right":
      return { x: maxX, y: MARGIN };
    case "center":
      return { x: (window.innerWidth - card.width) / 2, y: (window.innerHeight - card.height) / 2 };
    case "bottom-left":
      return { x: MARGIN, y: maxY };
    case "bottom-right":
      return { x: maxX, y: maxY };
  }
}

function zoneDistanceSq(zone: SnapZone, card: Size, centerX: number, centerY: number): number {
  const position = zonePosition(zone, card);
  const dx = position.x + card.width / 2 - centerX;
  const dy = position.y + card.height / 2 - centerY;
  return dx * dx + dy * dy;
}

function nearestZone(
  zones: readonly SnapZone[],
  card: Size,
  cardCenterX: number,
  cardCenterY: number,
): SnapZone {
  let nearest: SnapZone = zones[0] ?? "center";
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const zone of zones) {
    const distance = zoneDistanceSq(zone, card, cardCenterX, cardCenterY);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = zone;
    }
  }
  return nearest;
}

/**
 * Zone the card would land in given its current center and velocity.
 *
 * Slow drag: nearest zone to where the card sits — velocity is ignored
 * entirely so hand jitter never sways the result. Flick: project the center
 * along the velocity (momentum) and only consider zones ahead of the motion,
 * so even a short, sharp flick hops to the next zone in that direction and
 * never sends the card backwards.
 */
function landingZone(card: Size, centerX: number, centerY: number, velocity: Velocity): SnapZone {
  const speed = Math.hypot(velocity.x, velocity.y);
  if (speed < FLICK_SPEED) {
    return nearestZone(ZONES, card, centerX, centerY);
  }

  const projectedX = centerX + velocity.x * VELOCITY_PROJECTION;
  const projectedY = centerY + velocity.y * VELOCITY_PROJECTION;
  const ahead = ZONES.filter((zone) => {
    const position = zonePosition(zone, card);
    const dx = position.x + card.width / 2 - centerX;
    const dy = position.y + card.height / 2 - centerY;
    return dx * velocity.x + dy * velocity.y > 0;
  });
  return nearestZone(ahead.length > 0 ? ahead : ZONES, card, projectedX, projectedY);
}

export interface FloatingCardControls {
  /** Zone the card is currently snapped to. */
  zone: SnapZone;
  /** Animate the card to a zone (same spring as a drag release). */
  snapTo: (zone: SnapZone) => void;
}

interface FloatingCardProps {
  /** Pass a function to read the current zone and reposition the card. */
  children: ReactNode | ((controls: FloatingCardControls) => ReactNode);
  className?: string;
  "aria-label": string;
  initialZone?: SnapZone;
}

interface DragOverlayState {
  card: Size;
  nearest: SnapZone;
}

/**
 * Landing preview shown while dragging: a card-sized outline at the zone the
 * card would snap to on release (like OS window snapping). The outline
 * appears in place with a quick fade — it never slides across the screen, so
 * it always reads as "this is where it lands" rather than an object chasing
 * the card.
 */
function DropzonePreview({ card, nearest }: DragOverlayState) {
  const position = zonePosition(nearest, card);
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.12, ease: "easeOut" }}
      style={{ width: card.width, height: card.height, left: position.x, top: position.y }}
      className="pointer-events-none fixed z-[59] rounded-2xl border-2 border-dashed border-foreground/25 bg-foreground/[0.04]"
    />
  );
}

/**
 * Draggable floating card that snaps to one of five placement zones (the
 * four corners and center) with a spring. While dragging, a card-sized
 * outline previews exactly where the card will land — with hysteresis so it
 * never flickers between zones. Releasing a drag lands in the previewed
 * zone; a flick instead projects the release velocity so the card sails to
 * the zone it was thrown toward. The card re-anchors to its zone when the
 * viewport or its own content resizes.
 */
export function FloatingCard({
  children,
  className,
  "aria-label": ariaLabel,
  initialZone = "bottom-right",
}: FloatingCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const zoneRef = useRef<SnapZone>(initialZone);
  /** Zone the drop preview currently highlights (only set while dragging). */
  const previewZoneRef = useRef<SnapZone | null>(null);
  const draggingRef = useRef(false);
  const [currentZone, setCurrentZone] = useState<SnapZone>(initialZone);
  const [dragOverlay, setDragOverlay] = useState<DragOverlayState | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const snapTo = useCallback(
    (zone: SnapZone, immediate = false) => {
      const node = ref.current;
      if (!node) return;
      zoneRef.current = zone;
      setCurrentZone(zone);
      const target = zonePosition(zone, { width: node.offsetWidth, height: node.offsetHeight });
      if (immediate) {
        x.set(target.x);
        y.set(target.y);
      } else {
        // Inherit the current velocity so a released flick glides straight
        // into the zone instead of stopping dead and re-accelerating.
        animate(x, target.x, { ...SNAP_SPRING, velocity: x.getVelocity() });
        animate(y, target.y, { ...SNAP_SPRING, velocity: y.getVelocity() });
      }
    },
    [x, y],
  );

  // Place the card before first paint so it never flashes at the origin.
  // snapTo is stable and initialZone is constant, so this runs once.
  useLayoutEffect(() => snapTo(initialZone, true), [snapTo, initialZone]);

  // Keep the card anchored to its zone when the window or the card itself
  // changes size (tab switches, search results, keypad toggle).
  useEffect(() => {
    const onResize = () => {
      if (!draggingRef.current) snapTo(zoneRef.current, true);
    };
    window.addEventListener("resize", onResize);

    const observer = new ResizeObserver(() => {
      if (!draggingRef.current) snapTo(zoneRef.current);
    });
    if (ref.current) observer.observe(ref.current);

    return () => {
      window.removeEventListener("resize", onResize);
      observer.disconnect();
    };
  }, [snapTo]);

  const onDragStart = useCallback(() => {
    draggingRef.current = true;
    // Fast drags can sweep across page content and select text mid-gesture,
    // which reads as glitchy; suspend selection for the duration.
    document.body.style.userSelect = "none";
    const node = ref.current;
    if (!node) return;
    previewZoneRef.current = zoneRef.current;
    setDragOverlay({
      card: { width: node.offsetWidth, height: node.offsetHeight },
      nearest: zoneRef.current,
    });
  }, []);

  // The live preview uses the same velocity-aware landing logic as release,
  // so mid-flick the outline already shows the zone the flick will reach.
  // For slow drags the highlighted zone is sticky (see ZONE_STICKINESS): it
  // only hands off once the new zone is decisively closer, so the preview
  // never flickers while hovering between two zones.
  const onDrag = useCallback(
    (_event: unknown, info: PanInfo) => {
      const node = ref.current;
      if (!node) return;
      const card = { width: node.offsetWidth, height: node.offsetHeight };
      const centerX = x.get() + card.width / 2;
      const centerY = y.get() + card.height / 2;
      const zone = landingZone(card, centerX, centerY, info.velocity);

      setDragOverlay((prev) => {
        if (!prev || prev.nearest === zone) return prev;
        const isFlick = Math.hypot(info.velocity.x, info.velocity.y) >= FLICK_SPEED;
        if (!isFlick) {
          const currentDistance = zoneDistanceSq(prev.nearest, card, centerX, centerY);
          const candidateDistance = zoneDistanceSq(zone, card, centerX, centerY);
          if (candidateDistance > currentDistance * ZONE_STICKINESS) return prev;
        }
        previewZoneRef.current = zone;
        return { ...prev, nearest: zone };
      });
    },
    [x, y],
  );

  // The card always lands exactly where the preview said it would: slow
  // releases snap to the previewed (sticky) zone, and only a genuine flick
  // re-projects along the release velocity.
  const onDragEnd = useCallback(
    (_event: unknown, info: PanInfo) => {
      draggingRef.current = false;
      document.body.style.userSelect = "";
      setDragOverlay(null);
      const node = ref.current;
      if (!node) return;

      const card = { width: node.offsetWidth, height: node.offsetHeight };
      const isFlick = Math.hypot(info.velocity.x, info.velocity.y) >= FLICK_SPEED;
      const zone =
        !isFlick && previewZoneRef.current
          ? previewZoneRef.current
          : landingZone(card, x.get() + card.width / 2, y.get() + card.height / 2, info.velocity);
      previewZoneRef.current = null;
      snapTo(zone);
    },
    [snapTo, x, y],
  );

  // Portal to <body> so the card floats above modal dialogs/sheets, and
  // force pointer events back on: Radix modals set `pointer-events: none`
  // on <body>, which would otherwise make the card inert while one is open.
  return createPortal(
    <>
      {/* Keyed by zone so a target change crossfades between outlines. */}
      <AnimatePresence>
        {dragOverlay && (
          <DropzonePreview
            key={dragOverlay.nearest}
            card={dragOverlay.card}
            nearest={dragOverlay.nearest}
          />
        )}
      </AnimatePresence>
      <motion.div
        ref={ref}
        drag
        dragMomentum={false}
        dragElastic={0}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        style={{ x, y }}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        role="dialog"
        aria-label={ariaLabel}
        data-floating-card=""
        className={cn(
          "pointer-events-auto fixed left-0 top-0 z-[60] cursor-grab active:cursor-grabbing",
          className,
        )}
      >
        {typeof children === "function" ? children({ zone: currentZone, snapTo }) : children}
      </motion.div>
    </>,
    document.body,
  );
}
